import { useMemo } from 'react';

import { MenuOption, MenuSelectOption } from '@equip.health/ui';
import {
  toMenuOption,
  toMenuSelectOption,
  toStateMenuOption,
} from '~/lib/util/menu';
import {
  useGetContactPreferencesEnumQuery,
  useGetEthnicitiesEnumQuery,
  useGetGendersEnumQuery,
  useGetPronounsEnumQuery,
  useGetProviderLanguagesEnumQuery,
  useGetProviderStatesEnumQuery,
  useGetProviderTimezonesEnumQuery,
} from '~/store/services/enumerations';

const useEnumsForEditDuplicateSupportModal = (): {
  contactPreferencesOptions: MenuOption[];
  ethnicitiesOptions: MenuSelectOption[];
  gendersMenuOptions: MenuOption[];
  isGetContactPreferencesLoading: boolean;
  isGetEthnicitiesLoading: boolean;
  isGetGendersLoading: boolean;
  isGetPronounsLoading: boolean;
  isGetLanguagesLoading: boolean;
  isGetStatesLoading: boolean;
  isGetTimezonesLoading: boolean;
  languagesMenuOption: MenuSelectOption[];
  pronounsMenuOptions: MenuOption[];
  statesMenuOptions: MenuOption[];
  timezonesMenuOptions: MenuOption[];
} => {
  const { data: timezones, isLoading: isGetTimezonesLoading } =
    useGetProviderTimezonesEnumQuery();
  const { data: ethnicities, isLoading: isGetEthnicitiesLoading } =
    useGetEthnicitiesEnumQuery();
  const { data: genders, isLoading: isGetGendersLoading } =
    useGetGendersEnumQuery();
  const { data: languages, isLoading: isGetLanguagesLoading } =
    useGetProviderLanguagesEnumQuery();
  const { data: pronouns, isLoading: isGetPronounsLoading } =
    useGetPronounsEnumQuery();
  const { data: states, isLoading: isGetStatesLoading } =
    useGetProviderStatesEnumQuery();
  const {
    data: contactPreferences,
    isLoading: isGetContactPreferencesLoading,
  } = useGetContactPreferencesEnumQuery();

  const gendersMenuOptions = useMemo<MenuOption[]>(
    () => genders?.map((g) => g.label)?.map(toMenuOption) ?? [],
    [genders],
  );

  const pronounsMenuOptions = useMemo<MenuOption[]>(
    () => pronouns?.map(toMenuOption) ?? [],
    [pronouns],
  );

  const ethnicitiesOptions = useMemo<MenuSelectOption[]>(
    () => ethnicities?.map((e) => e.label)?.map(toMenuSelectOption) ?? [],
    [ethnicities],
  );

  const contactPreferencesOptions = useMemo<MenuOption[]>(
    () => contactPreferences?.map(toMenuOption) ?? [],
    [contactPreferences],
  );

  const languagesMenuOption = useMemo<MenuSelectOption[]>(
    () => languages?.map((l) => l.label)?.map(toMenuOption) ?? [],
    [languages],
  );

  const timezonesMenuOptions = useMemo<MenuOption[]>(
    () => timezones?.map((tz) => ({ node: tz.label, value: tz.label })) ?? [],
    [timezones],
  );

  const statesMenuOptions = useMemo<MenuOption[]>(
    () => states?.map(toStateMenuOption) ?? [],
    [states],
  );

  return {
    contactPreferencesOptions,
    ethnicitiesOptions,
    gendersMenuOptions,
    isGetContactPreferencesLoading,
    isGetEthnicitiesLoading,
    isGetGendersLoading,
    isGetPronounsLoading,
    isGetLanguagesLoading,
    isGetStatesLoading,
    isGetTimezonesLoading,
    languagesMenuOption,
    pronounsMenuOptions,
    statesMenuOptions,
    timezonesMenuOptions,
  };
};

export default useEnumsForEditDuplicateSupportModal;
